import type { Chord } from '@/types/chord';
import type { Move, MoveContext, MoveResult } from '../types';

/**
 * darken it — modal mixture.
 *
 * v1 covers the two idiomatic pre-cadence mixtures named in MOVES-SPEC.md:
 * IV -> iv and vi -> "borrowed VI". The spec's applicability line reads
 * "major-mode diatonic major triad", but that can't be literally true for
 * both examples — IV is naturally major, vi is naturally minor (see
 * getNaturalQuality). Applicability here is keyed off each example's actual
 * natural quality instead.
 *
 * vi -> "VI": true bVI (e.g. Ab major in C major) has a root a semitone
 * below the diatonic vi, which this Chord model has no field to represent
 * (see pitch-utils.ts). This mirrors the existing convention already used
 * by the hardcoded demo chord at App.tsx:75 — same scale-degree, quality
 * flipped to major, chromaticType 'borrowed' — rather than a literal
 * flatted root.
 */
export const darkenIt: Move = {
  id: 'darken-it',
  name: 'darken it',
  theory: 'modal mixture',

  applicable(ctx: MoveContext): boolean {
    const chord = ctx.chords[ctx.index];
    if (!chord || ctx.mode !== 'major' || chord.isChromatic) return false;
    return (
      (chord.scaleDegree === 4 && chord.quality === 'major') ||
      (chord.scaleDegree === 6 && chord.quality === 'minor')
    );
  },

  apply(ctx: MoveContext): MoveResult {
    const chord = ctx.chords[ctx.index];
    const isIV = chord.scaleDegree === 4;

    const changed: Chord = {
      ...chord,
      quality: isIV ? 'minor' : 'major',
      isChromatic: true,
      chromaticType: 'borrowed',
      key: ctx.key,
      mode: ctx.mode,
    };
    const chords: Chord[] = ctx.chords.map((c, i) => (i === ctx.index ? changed : c));

    return {
      chords,
      changedIds: [changed.id],
      explanation: isIV
        ? `borrowed the minor iv from ${ctx.key} minor — the pre-cadence darkening.`
        : `borrowed the major VI from ${ctx.key} minor — a shadow with a bright edge.`,
      theoryLabel: isIV ? 'modal mixture · IV → iv' : 'modal mixture · vi → VI',
    };
  },
};
